// Admin Imports
import CompanyDetails from "views/admin/company";
import AiCapabilitiesPage from "views/admin/configure/ai-capabilities";
import AiGatewayPage from "views/admin/configure/ai-gateway";
import BudgetsPage from "views/admin/configure/budgets";
import AiSpendPage from "views/admin/dashboard/ai-spend";
import AutonomousAgents from "views/admin/configure/autonomous-agents";
import EmbedPage from "views/admin/configure/embed";
import PromptShortcutsPage from "views/admin/configure/prompt-shortcuts";
import UxPage from "views/admin/configure/ux";
import Customers from "views/admin/customers";
import CustomerChatDetails from "views/admin/customers/chat-details";
import CustomerDetails from "views/admin/customers/details";
import CustomerImport from "views/admin/customers/import";
import CustomerCompanies from "views/admin/customer-companies";
import CustomerCompanyDetails from "views/admin/customer-companies/details";
import NewCustomerCompany from "views/admin/customer-companies/new";
import DashboardOverview from "views/admin/dashboard/overview";
import DashboardUsage from "views/admin/dashboard/usage";
import DashboardPerformance from "views/admin/dashboard/performance";
import FeatureFlags from "views/admin/feature-flags";
import ToolMappings from "views/admin/feature-flags/mappings";
import ToolPreview from "views/admin/feature-flags/preview";
import Feedback from "views/admin/feedback";
import Integrations from "views/admin/integrations";
import Notifications from "views/admin/notifications";
import KnowledgeBase from "views/admin/knowledge-base";
import Logs from "views/admin/logs";
import LogDetails from "views/admin/logs/details";
import PolicyViolations from "views/admin/policy-violations";
import PolicyViolationDetails from "views/admin/policy-violations/details";
import Profile from "views/admin/profile";
import Guardrails from "views/admin/security/guardrails";
import Monitoring from "views/admin/security/masking";
import Servers from "views/admin/servers";
import AuthorizationLogs from "views/admin/servers/authorization-logs";
import HelpPage from "views/admin/help";
import Skills from "views/admin/skills";
import SuggestionsPage from "views/admin/suggestions";
import AgentJobs from "views/admin/agent-jobs";
import AgentJobDetails from "views/admin/agent-jobs/details";
import ToolExecutionLogs from "views/admin/tool-execution-logs";
import ToolExecutionLogDetails from "views/admin/tool-execution-logs/details";
import UsersManagement from "views/admin/users";
// Auth Imports
import AcceptInvitation from "views/auth/AcceptInvitation";
import ForgotPassword from "views/auth/ForgotPassword";
import ResetPassword from "views/auth/ResetPassword";
import SignIn from "views/auth/SignIn";
import SignUp from "views/auth/SignUp";
import VerifyEmail from "views/auth/VerifyEmail";
import { Navigate } from "react-router-dom";

// Icon Imports
import {
  MdApartment,
  MdAutoAwesome,
  MdAccountBalanceWallet,
  MdBarChart,
  MdBolt,
  MdBusiness,
  MdDesignServices,
  MdFlag,
  MdFormatQuote,
  MdGroup,
  MdHistory,
  MdHome,
  MdHub,
  MdInsights,
  MdSpeed,
  MdLibraryBooks,
  MdLightbulb,
  MdLink,
  MdList,
  MdNotifications,
  MdPaid,
  MdLock,
  MdPeople,
  MdPerson,
  MdPreview,
  MdSecurity,
  MdShield,
  MdSmartToy,
  MdLiveHelp,
  MdStorage,
  MdThumbUp,
  MdTune,
  MdVisibility,
  MdWarning,
} from "react-icons/md";

// Route names are translation keys that will be resolved by the sidebar/navbar components
const routes = [
  // Redirect the legacy /admin/dashboard entry to the Overview sub-page.
  {
    name: "app.navigation.dashboard",
    layout: "/admin",
    path: "dashboard",
    icon: <MdHome className="h-6 w-6" />,
    invisible: true,
    component: <Navigate to="/admin/dashboard/overview" replace />,
  },

  // ── Dashboard section (Overview / Usage / Performance) ───────────────────
  {
    name: "app.navigation.dashboard",
    layout: "/admin",
    path: "dashboard-section",
    icon: <MdHome className="h-6 w-6" />,
    isSection: true,
    children: [
      {
        name: "app.navigation.dashboardOverview",
        layout: "/admin",
        path: "dashboard/overview",
        icon: <MdInsights className="h-6 w-6" />,
        component: <DashboardOverview />,
      },
      {
        name: "app.navigation.dashboardUsage",
        layout: "/admin",
        path: "dashboard/usage",
        icon: <MdBarChart className="h-6 w-6" />,
        component: <DashboardUsage />,
      },
      {
        name: "app.navigation.dashboardPerformance",
        layout: "/admin",
        path: "dashboard/performance",
        icon: <MdSpeed className="h-6 w-6" />,
        component: <DashboardPerformance />,
      },
      {
        name: "app.navigation.aiSpend",
        layout: "/admin",
        path: "dashboard/ai-spend",
        icon: <MdPaid className="h-6 w-6" />,
        component: <AiSpendPage />,
      },
    ],
  },

  // ── Configure section ────────────────────────────────────────────────────
  {
    name: "app.navigation.configure",
    layout: "/admin",
    path: "configure-section",
    icon: <MdTune className="h-6 w-6" />,
    isSection: true,
    children: [
      {
        name: "app.navigation.toolkit",
        layout: "/admin",
        path: "servers",
        icon: <MdStorage className="h-6 w-6" />,
        component: <Servers />,
      },
      {
        name: "app.navigation.aiCapabilities",
        layout: "/admin",
        path: "configure/agents",
        icon: <MdSmartToy className="h-6 w-6" />,
        component: <AiCapabilitiesPage />,
      },
      {
        name: "app.navigation.aiGateway",
        layout: "/admin",
        path: "configure/ai-gateway",
        icon: <MdHub className="h-6 w-6" />,
        component: <AiGatewayPage />,
        requiredFeature: "aiGateway",
      },
      {
        name: "app.navigation.skills",
        layout: "/admin",
        path: "skills",
        icon: <MdAutoAwesome className="h-6 w-6" />,
        component: <Skills />,
      },
      {
        name: "app.navigation.autonomousAgents",
        layout: "/admin",
        path: "configure/autonomous-agents",
        icon: <MdBolt className="h-6 w-6" />,
        component: <AutonomousAgents />,
      },
      {
        name: "app.navigation.ux",
        layout: "/admin",
        path: "configure/user-interface",
        icon: <MdDesignServices className="h-6 w-6" />,
        component: <UxPage />,
      },
      {
        name: "app.navigation.promptShortcuts",
        layout: "/admin",
        path: "configure/prompt-shortcuts",
        icon: <MdFormatQuote className="h-6 w-6" />,
        component: <PromptShortcutsPage />,
      },
      {
        name: "app.navigation.knowledgeBase",
        layout: "/admin",
        path: "knowledge-base",
        icon: <MdLibraryBooks className="h-6 w-6" />,
        component: <KnowledgeBase />,
        requiredFeature: "knowledgeBase",
      },

      // ── Integrations ─────────────────────────────────────────────────────────
      {
        name: "app.navigation.integrations",
        layout: "/admin",
        path: "integrations",
        icon: <MdLink className="h-6 w-6" />,
        component: <Integrations />,
        requiredFeature: "handoffIntegrations",
      },
      {
        name: "app.navigation.notifications",
        layout: "/admin",
        path: "notifications",
        icon: <MdNotifications className="h-6 w-6" />,
        component: <Notifications />,
      },
      {
        name: "app.navigation.suggestions",
        layout: "/admin",
        path: "suggestions",
        icon: <MdLightbulb className="h-6 w-6" />,
        component: <SuggestionsPage />,
      },
      {
        name: "app.navigation.budgets",
        layout: "/admin",
        path: "configure/budgets",
        icon: <MdAccountBalanceWallet className="h-6 w-6" />,
        component: <BudgetsPage />,
      },
      {
        name: "app.navigation.embed",
        layout: "/admin",
        path: "configure/embed",
        icon: <MdLink className="h-6 w-6" />,
        component: <EmbedPage />,
      },
    ],
  },

  // Hidden child routes (still routable, not in nav)
  {
    name: "app.navigation.authorizationLogs",
    layout: "/admin",
    path: "servers/authorization-logs/:serverId",
    icon: <MdList className="h-6 w-6" />,
    component: <AuthorizationLogs />,
    invisible: true,
  },
  // ── Security section ──────────────────────────────────────────────────────
  {
    name: "app.navigation.security",
    layout: "/admin",
    path: "security-section",
    icon: <MdSecurity className="h-6 w-6" />,
    isSection: true,
    children: [
      {
        name: "app.navigation.guardrails",
        layout: "/admin",
        path: "security/guardrails",
        icon: <MdShield className="h-6 w-6" />,
        component: <Guardrails />,
      },
      {
        name: "app.navigation.dataMasking",
        layout: "/admin",
        path: "security/data-privacy",
        icon: <MdVisibility className="h-6 w-6" />,
        component: <Monitoring />,
      },
      {
        name: "app.navigation.toolPolicies",
        layout: "/admin",
        path: "feature-flags",
        icon: <MdFlag className="h-6 w-6" />,
        component: <FeatureFlags />,
      },
    ],
  },

  // ── Analytics section ─────────────────────────────────────────────────────
  {
    name: "app.navigation.analytics",
    layout: "/admin",
    path: "analytics-section",
    icon: <MdBarChart className="h-6 w-6" />,
    isSection: true,
    children: [
      {
        name: "app.navigation.feedback",
        layout: "/admin",
        path: "feedback",
        icon: <MdThumbUp className="h-6 w-6" />,
        component: <Feedback />,
      },
      {
        name: "app.navigation.aiActions",
        layout: "/admin",
        path: "ai-logs",
        icon: <MdHistory className="h-6 w-6" />,
        component: <Logs />,
      },
      {
        name: "app.navigation.toolRuns",
        layout: "/admin",
        path: "tool-execution-logs",
        icon: <MdList className="h-6 w-6" />,
        component: <ToolExecutionLogs />,
      },
      {
        name: "app.navigation.agentJobs",
        layout: "/admin",
        path: "agent-jobs",
        icon: <MdAutoAwesome className="h-6 w-6" />,
        component: <AgentJobs />,
      },
      {
        name: "app.navigation.violations",
        layout: "/admin",
        path: "policy-violations",
        icon: <MdWarning className="h-6 w-6" />,
        component: <PolicyViolations />,
      },
    ],
  },

  // ── CRM ───────────────────────────────────────────────────────────────────
  {
    name: "app.navigation.crm",
    layout: "/admin",
    path: "customers-section",
    icon: <MdGroup className="h-6 w-6" />,
    isSection: true,
    children: [
      {
        name: "app.navigation.customerCompanies",
        layout: "/admin",
        path: "customer-companies",
        icon: <MdApartment className="h-6 w-6" />,
        component: <CustomerCompanies />,
      },
      {
        name: "app.navigation.customers",
        layout: "/admin",
        path: "customers",
        icon: <MdGroup className="h-6 w-6" />,
        component: <Customers />,
      },
    ],
  },

  // ── Hidden detail/sub routes ──────────────────────────────────────────────
  {
    name: "app.navigation.toolMappings",
    layout: "/admin",
    path: "feature-flags/mappings",
    icon: <MdLink className="h-6 w-6" />,
    component: <ToolMappings />,
    invisible: true,
  },
  {
    name: "app.navigation.toolAccessSimulation",
    layout: "/admin",
    path: "feature-flags/preview",
    icon: <MdPreview className="h-6 w-6" />,
    component: <ToolPreview />,
    invisible: true,
  },
  {
    name: "app.navigation.customerDetails",
    layout: "/admin",
    path: "customers/:id",
    icon: <MdGroup className="h-6 w-6" />,
    component: <CustomerDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.customerImport",
    layout: "/admin",
    path: "customers-import",
    icon: <MdGroup className="h-6 w-6" />,
    component: <CustomerImport />,
    invisible: true,
  },
  {
    name: "app.navigation.newCustomerCompany",
    layout: "/admin",
    path: "customer-companies/new",
    icon: <MdGroup className="h-6 w-6" />,
    component: <NewCustomerCompany />,
    invisible: true,
  },
  {
    name: "app.navigation.customerCompanyDetails",
    layout: "/admin",
    path: "customer-companies/:id",
    icon: <MdGroup className="h-6 w-6" />,
    component: <CustomerCompanyDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.customerChatDetails",
    layout: "/admin",
    path: "customers/:customerId/chats/:chatId",
    icon: <MdGroup className="h-6 w-6" />,
    component: <CustomerChatDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.logDetails",
    layout: "/admin",
    path: "ai-logs/:id",
    icon: <MdList className="h-6 w-6" />,
    component: <LogDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.toolExecutionLogDetails",
    layout: "/admin",
    path: "tool-execution-logs/:id",
    icon: <MdList className="h-6 w-6" />,
    component: <ToolExecutionLogDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.agentJobDetails",
    layout: "/admin",
    path: "agent-jobs/:id",
    icon: <MdAutoAwesome className="h-6 w-6" />,
    component: <AgentJobDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.violationDetails",
    layout: "/admin",
    path: "policy-violations/:id",
    icon: <MdWarning className="h-6 w-6" />,
    component: <PolicyViolationDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.company",
    layout: "/admin",
    path: "company",
    icon: <MdBusiness className="h-6 w-6" />,
    component: <CompanyDetails />,
    invisible: true,
  },
  {
    name: "app.navigation.users",
    layout: "/admin",
    path: "users",
    icon: <MdPeople className="h-6 w-6" />,
    component: <UsersManagement />,
    invisible: true,
  },
  {
    name: "app.navigation.profile",
    layout: "/admin",
    path: "profile",
    icon: <MdPerson className="h-6 w-6" />,
    component: <Profile />,
    invisible: true,
  },
  {
    name: "app.navigation.help",
    layout: "/admin",
    path: "help",
    icon: <MdLiveHelp className="h-6 w-6" />,
    component: <HelpPage />,
    invisible: true,
  },
  {
    name: "app.auth.signIn",
    layout: "/auth",
    path: "sign-in",
    icon: <MdLock className="h-6 w-6" />,
    component: <SignIn />,
    invisible: true,
  },
  {
    name: "app.auth.signUp",
    layout: "/auth",
    path: "sign-up",
    icon: <MdLock className="h-6 w-6" />,
    component: <SignUp />,
    invisible: true,
  },
  {
    name: "app.auth.acceptInvitation.title",
    layout: "/auth",
    path: "accept-invitation",
    icon: <MdLock className="h-6 w-6" />,
    component: <AcceptInvitation />,
    invisible: true,
  },
  {
    name: "app.auth.forgotPassword.title",
    layout: "/auth",
    path: "forgot-password",
    icon: <MdLock className="h-6 w-6" />,
    component: <ForgotPassword />,
    invisible: true,
  },
  {
    name: "app.auth.resetPassword.title",
    layout: "/auth",
    path: "reset-password",
    icon: <MdLock className="h-6 w-6" />,
    component: <ResetPassword />,
    invisible: true,
  },
  {
    name: "app.auth.verifyEmail.title",
    layout: "/auth",
    path: "verify-email",
    icon: <MdLock className="h-6 w-6" />,
    component: <VerifyEmail />,
    invisible: true,
  },
];

export default routes;
